// The phone hub: one phone per browser, shared by every console tab (REQ-001: the call carries on while the operator
// moves between pages, opens another tab, or reloads one). It runs in a SharedWorker (phone-worker.ts); where there is
// none, in the page itself. No DOM, no credentials: server calls go through a connected tab.
//
//  - Tabs connect with a port and say hello; each gets the phone's state on every change.
//  - Commands from any tab (dial, answer, hold, ...) go to the one engine. Their outcome goes back to that tab.
//  - With an engine that doesn't record its own controls (the Web Phone), every control and its outcome is reported to
//    POST /api/softphone/control, whether it worked or not (REQ-024: call control on record).
//  - A tab that stops pinging is dropped. When the last one goes, the phone stays registered for as long as the browser
//    keeps the worker: a reload reconnects to the same phone and the same calls.
//  - A hang-up asked for from another screen arrives as a 'softphone' command message: the phone holding the call
//    hangs it up and reports it.

import type { ControlAction, ControlArgs, EngineHost, HubMessage, MediaArgs, MediaOp, PhoneEngine, PhoneState, PortLike, Registration, TabMessage } from './engine.ts';
import { DTMF, IDLE, phonePathAllowed } from './engine.ts';

const HTTP_TIMEOUT_MS = 15_000;
const MEDIA_TIMEOUT_MS = 20_000;
const TAB_SILENT_MS = 30_000;
const CONTROLS: readonly ControlAction[] = ['answer', 'decline', 'to_voicemail', 'hangup', 'mute', 'unmute', 'hold', 'unhold', 'dtmf', 'transfer', 'warm_transfer_start', 'warm_transfer_complete', 'warm_transfer_cancel'];

interface Tab {
  id: string;
  port: PortLike;
  lastSeen: number;
  lastActive: number;
}

interface PendingHttp {
  req: { method: 'GET' | 'POST'; path: string; body?: unknown };
  tab: string;
  retried: boolean;
  timer: ReturnType<typeof setTimeout>;
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
}

export interface HubOptions {
  makeEngine: (name: 'simulator' | 'ringcentral', host: EngineHost) => PhoneEngine;
  now?: () => number;
  /** For tests: no interval timers. */
  timers?: boolean;
}

export class PhoneHub {
  private tabs = new Map<string, Tab>();
  private pending = new Map<string, PendingHttp>();
  private media = new Map<string, { tab: string; timer: ReturnType<typeof setTimeout>; resolve: (v: { data: unknown; tab: string }) => void; reject: (e: Error) => void }>();
  private engine: PhoneEngine | null = null;
  private registration: Registration = 'off';
  private error: string | null = null;
  private seq = 0;
  /** Stream messages seen lately: every tab forwards the same one. */
  private seen = new Map<string, number>();
  private readonly now: () => number;
  private sweeper: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly o: HubOptions) {
    this.now = o.now ?? Date.now;
    if (o.timers !== false) this.sweeper = setInterval(() => this.sweep(), 10_000);
  }

  /** A tab connected (SharedWorker onconnect, or the in-page fallback). */
  attach(port: PortLike): void {
    port.onmessage = (e) => void this.onMessage(port, e.data as TabMessage).catch((err) => console.error('phone hub message failed', err));
    port.start?.();
  }

  state(): PhoneState {
    if (!this.engine) return { ...IDLE, registration: this.registration, error: this.error, tabs: this.tabs.size };
    return {
      engine: this.engine.name,
      simulated: this.engine.simulated,
      registration: this.registration,
      error: this.error,
      calls: this.engine.calls(),
      audioTab: this.audioTab(),
      tabs: this.tabs.size,
    };
  }

  /** The tab that plays audio and makes server calls: the one used most recently. */
  private audioTab(): string | null {
    let best: Tab | null = null;
    for (const t of this.tabs.values()) if (!best || t.lastActive > best.lastActive) best = t;
    return best?.id ?? null;
  }

  private broadcast() {
    const m: HubMessage = { t: 'state', state: this.state() };
    for (const t of this.tabs.values()) this.send(t.port, m);
  }

  private send(port: PortLike, m: HubMessage) {
    try {
      port.postMessage(m);
    } catch {
      /* a closed port is swept */
    }
  }

  private sweep() {
    const cutoff = this.now() - TAB_SILENT_MS;
    let gone = false;
    for (const [id, t] of this.tabs) if (t.lastSeen < cutoff) gone = this.drop(id) || gone;
    if (gone) this.broadcast();
  }

  private drop(tabId: string): boolean {
    if (!this.tabs.delete(tabId)) return false;
    // Server calls that tab was making are retried on another; its media operations fail (the engine moves the call).
    for (const [id, p] of this.pending) if (p.tab === tabId) this.retry(id);
    for (const [id, m] of this.media)
      if (m.tab === tabId) {
        clearTimeout(m.timer);
        this.media.delete(id);
        m.reject(new Error('the tab playing the call went away'));
      }
    this.engine?.tabsChanged?.([...this.tabs.keys()]);
    return true;
  }

  private touch(tabId: string, active = false) {
    const t = this.tabs.get(tabId);
    if (!t) return;
    t.lastSeen = this.now();
    if (active) t.lastActive = this.now();
  }

  // ───────── server calls, through a tab ─────────

  private readonly host: EngineHost = {
    http: <T>(method: 'GET' | 'POST', path: string, body?: unknown) => this.http<T>(method, path, body),
    media: (op: MediaOp, args: MediaArgs, preferTab: string | null) => this.mediaOp(op, args, preferTab),
  };

  /** A WebRTC operation in a tab: the call's own tab while it is connected, else the tab in use. */
  private mediaOp(op: MediaOp, args: MediaArgs, preferTab: string | null): Promise<{ data: unknown; tab: string }> {
    const tab = (preferTab && this.tabs.get(preferTab)) || [...this.tabs.values()].sort((a, b) => b.lastActive - a.lastActive)[0];
    if (!tab) return Promise.reject(new Error('no console tab is open to play the call'));
    const id = `m${++this.seq}`;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.media.delete(id);
        reject(new Error(`the call's audio did not answer (${op})`));
      }, MEDIA_TIMEOUT_MS);
      this.media.set(id, { tab: tab.id, timer, resolve, reject });
      this.send(tab.port, { t: 'media', id, op, args });
    });
  }

  private http<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    if (!phonePathAllowed(path)) return Promise.reject(new Error(`the phone may not call ${path}`));
    return new Promise<T>((resolve, reject) => this.sendHttp({ method, path, body }, false, resolve as (v: unknown) => void, reject));
  }

  private sendHttp(req: PendingHttp['req'], retried: boolean, resolve: (v: unknown) => void, reject: (e: Error) => void, avoid?: string) {
    const tab = [...this.tabs.values()].filter((t) => t.id !== avoid).sort((a, b) => b.lastActive - a.lastActive)[0];
    if (!tab) return reject(new Error('no console tab is open to reach the server'));
    const id = `h${++this.seq}`;
    const timer = setTimeout(() => this.retry(id), HTTP_TIMEOUT_MS);
    this.pending.set(id, { req, tab: tab.id, retried, timer, resolve, reject });
    this.send(tab.port, { t: 'http', id, ...req });
  }

  /** The tab making a server call went away or didn't answer: once more on another tab, else fail. */
  private retry(id: string) {
    const p = this.pending.get(id);
    if (!p) return;
    clearTimeout(p.timer);
    this.pending.delete(id);
    if (p.retried) return p.reject(new Error('the server did not answer'));
    this.sendHttp(p.req, true, p.resolve, p.reject, p.tab);
  }

  // ───────── commands ─────────

  private async ensureEngine(name: 'simulator' | 'ringcentral'): Promise<PhoneEngine> {
    if (this.engine && this.engine.name === name) return this.engine;
    if (this.engine) await this.engine.stop().catch(() => undefined);
    const e = this.o.makeEngine(name, this.host);
    e.onChange(() => this.broadcast());
    e.onStatus?.((registration, error) => {
      if (this.registration === 'off') return;
      this.registration = registration;
      this.error = error;
      this.broadcast();
    });
    this.engine = e;
    return e;
  }

  private async start(name: 'simulator' | 'ringcentral') {
    const e = await this.ensureEngine(name);
    if (this.registration === 'registered' || this.registration === 'starting') return;
    this.registration = 'starting';
    this.error = null;
    this.broadcast();
    try {
      await e.start();
      this.registration = 'registered';
    } catch (err) {
      const msg = (err as Error).message;
      this.registration = /sign in/i.test(msg) ? 'needs_sign_in' : 'error';
      this.error = msg;
    }
    this.broadcast();
  }

  /** Reports a control and its outcome when the engine doesn't record its own. A report that fails is logged: the action already happened. */
  private async report(callId: string | null, action: ControlAction | 'dial' | 'reconnect', outcome: 'ok' | 'failed', args: ControlArgs, error: string | null) {
    if (!this.engine || this.engine.recordsOwnControls) return;
    const call = callId ? this.engine.calls().find((c) => c.id === callId) : null;
    const body: Record<string, unknown> = { action, outcome, telephonySessionId: call?.telephonySessionId ?? null, clientAt: new Date(this.now()).toISOString() };
    if (args.to) body.to = args.to;
    // DTMF may be a passcode: only how many digits were sent leaves the browser.
    if (args.digits) body.digitCount = args.digits.length;
    if (args.reason) body.reason = args.reason;
    if (error) body.error = error;
    await this.http('POST', '/api/softphone/control', body).catch((err) => console.error('could not record a call control', err));
  }

  private async control(callId: string, action: ControlAction, args: ControlArgs) {
    const e = this.engine;
    if (!e || this.registration !== 'registered') throw new Error('the phone is not connected');
    if (!CONTROLS.includes(action)) throw new Error(`unknown action ${action}`);
    if (action === 'dtmf' && !DTMF.test(args.digits ?? '')) throw new Error('DTMF digits are 0-9, *, # and A-D');
    try {
      const r = await e.control(callId, action, args);
      await this.report(callId, action, 'ok', args, null);
      return r;
    } catch (err) {
      await this.report(callId, action, 'failed', args, (err as Error).message);
      throw err;
    }
  }

  private async dial(to: string, callerId: string | null) {
    const e = this.engine;
    if (!e || this.registration !== 'registered') throw new Error('the phone is not connected');
    try {
      const c = await e.dial(to, callerId);
      await this.report(c?.id ?? null, 'dial', 'ok', { to }, null);
      return c;
    } catch (err) {
      await this.report(null, 'dial', 'failed', { to }, (err as Error).message);
      throw err;
    }
  }

  /** Stops the phone and starts it again: a new registration, for whichever extension the agent now has. */
  private async reregister() {
    const name = this.engine?.name;
    if (!name) return;
    await this.engine?.stop().catch(() => undefined);
    this.engine = null;
    this.registration = 'off';
    this.broadcast();
    await this.start(name);
  }

  private async recover() {
    const e = this.engine;
    if (!e || this.registration === 'off' || this.registration === 'needs_sign_in') return;
    this.registration = 'reconnecting';
    this.broadcast();
    try {
      await e.recover();
      this.registration = 'registered';
      this.error = null;
      await this.report(null, 'reconnect', 'ok', {}, null);
    } catch (err) {
      this.registration = 'error';
      this.error = (err as Error).message;
      await this.report(null, 'reconnect', 'failed', {}, this.error);
    }
    this.broadcast();
  }

  /**
   * A 'softphone' stream message: the simulator's calls changed, a hang-up asked for from the console, or the agent's
   * workstation changed (the phone registers again as the new workstation's, or the agent's own).
   */
  private async server(data: unknown) {
    const d = data as { type?: string; action?: string; telephonySessionId?: string } | null;
    if (!this.engine) return;
    const key = JSON.stringify(data);
    const at = this.now();
    for (const [k, t] of this.seen) if (at - t > 5000) this.seen.delete(k);
    if (this.seen.has(key)) return;
    this.seen.set(key, at);
    if (d?.type === 'seat') return this.reregister();
    if (d?.type === 'command' && d.action === 'hangup' && d.telephonySessionId) {
      const call = this.engine.calls().find((c) => c.telephonySessionId === d.telephonySessionId && c.state !== 'ended');
      if (call) await this.control(call.id, 'hangup', { reason: 'console_request' }).catch(() => undefined);
      return;
    }
    this.engine.serverMessage(data);
  }

  private async onMessage(port: PortLike, m: TabMessage) {
    switch (m.t) {
      case 'hello': {
        const t = this.tabs.get(m.tabId);
        if (t) t.port = port;
        else this.tabs.set(m.tabId, { id: m.tabId, port, lastSeen: this.now(), lastActive: m.visible ? this.now() : 0 });
        this.engine?.tabsChanged?.([...this.tabs.keys()]);
        this.send(port, { t: 'state', state: this.state() });
        if (!this.engine || this.registration === 'off' || this.registration === 'error') await this.start(m.engine);
        else this.broadcast();
        return;
      }
      case 'ping':
        return this.touch(m.tabId);
      case 'active':
        this.touch(m.tabId, true);
        return this.broadcast();
      case 'bye':
        if (this.drop(m.tabId)) this.broadcast();
        return;
      case 'media_result': {
        const p = this.media.get(m.id);
        if (!p) return;
        clearTimeout(p.timer);
        this.media.delete(m.id);
        if (m.ok) p.resolve({ data: m.data, tab: p.tab });
        else p.reject(new Error(m.error ?? 'audio failed'));
        return;
      }
      case 'http_result': {
        const p = this.pending.get(m.id);
        if (!p) return;
        clearTimeout(p.timer);
        this.pending.delete(m.id);
        if (m.ok) p.resolve(m.data);
        else p.reject(Object.assign(new Error(m.error ?? 'request failed'), { status: m.status }));
        return;
      }
      case 'server':
        return this.server(m.data);
      case 'cmd': {
        const reply = (ok: boolean, data?: unknown, error?: string) => this.send(port, { t: 'result', id: m.id, ok, data, error });
        try {
          const a = m.args ?? {};
          switch (m.op) {
            case 'start':
              await this.start((a.engine as 'simulator' | 'ringcentral') ?? this.engine?.name ?? 'simulator');
              return reply(this.registration === 'registered', undefined, this.error ?? undefined);
            case 'stop':
              await this.engine?.stop();
              this.engine = null;
              this.registration = 'off';
              this.broadcast();
              return reply(true);
            case 'recover':
              await this.recover();
              return reply(true);
            case 'dial':
              return reply(true, await this.dial(String(a.to ?? ''), (a.callerId as string | null | undefined) ?? null));
            case 'control':
              return reply(true, await this.control(String(a.callId ?? ''), a.action as ControlAction, (a.args as ControlArgs) ?? {}));
          }
        } catch (err) {
          return reply(false, undefined, (err as Error).message);
        }
      }
    }
  }

  dispose() {
    if (this.sweeper) clearInterval(this.sweeper);
    for (const p of this.pending.values()) clearTimeout(p.timer);
    for (const m of this.media.values()) clearTimeout(m.timer);
    void this.engine?.stop();
  }
}
